import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';

import { getCalendarDetails, getCalendars } from '../api/services/Calendars';
import * as Constants from '../constants';

export type CalendarOption = {
  label: string;
  value: string | number | boolean;
};

const PER_PAGE = 20;

export const useCalendarOptions = (initialValues?: CalendarOption) => {
  const [calendarOptions, setCalendarOptions] = useState<CalendarOption[]>(initialValues ? [initialValues] : []);
  const [isLoading, setIsLoading] = useState(true);
  const abortController = useRef<AbortController>();
  const [queryParams, setQueryParams] = useState<CalendarQueryParams>({
    page: 1,
    perPage: PER_PAGE,
    q: undefined,
  });
  const debounceHandleQuery = useMemo(() => debounce(handleQuery, Constants.DEBOUNCE_TIME), []);

  function handleQuery(query) {
    if (!query) {
      setIsLoading(true);
      setCalendarOptions([]);
      setQueryParams((prevQueryParams) => ({
        q: undefined,
        page: 1,
        perPage: prevQueryParams.perPage,
      }));
    }

    if (query !== queryParams.q && query.length >= Constants.MINIMUM_SEARCH_STRING_LENGTH) {
      setIsLoading(true);
      setCalendarOptions([]);
      setQueryParams((prevQueryParams) => ({
        q: query,
        page: 1,
        perPage: prevQueryParams.perPage,
      }));
    }
  }

  const handlePagination = () => {
    setIsLoading(true);
    setQueryParams((prevQueryParams) => ({
      q: prevQueryParams.q,
      page: prevQueryParams.page + 1,
      perPage: queryParams.perPage,
    }));
  };

  const fetchCalendars = useCallback(async () => {
    abortController.current?.abort();
    abortController.current = new AbortController();
    const response = await getCalendars(queryParams, abortController.current.signal);

    if (response?.type === 'success') {
      const calendarRecords = response?.data.calendars;

      if (calendarRecords?.length > 0) {
        const formattedCalendars = formatCalendars(calendarRecords);

        setCalendarOptions((prevCalendars) => [
          ...new Map([...prevCalendars, ...formattedCalendars].map((calendar) => [calendar.value, calendar])).values(),
        ]);
      }
    }
    setIsLoading(false);
  }, [queryParams]);

  const addMissingCalendar = async (slug) => {
    if (calendarOptions.find((calendar) => calendar.value === slug)) {
      return;
    }

    const calendarResponse = await getCalendarDetails(slug);
    const formattedCalendar = formatCalendars([calendarResponse?.data?.calendar]);

    setCalendarOptions((prevCalendars) => [
      ...new Map([...prevCalendars, ...formattedCalendar].map((calendar) => [calendar.value, calendar])).values(),
    ]);
  };

  useEffect(() => {
    fetchCalendars();
  }, [fetchCalendars]);

  useEffect(() => {
    return () => {
      abortController.current?.abort();
    };
  }, []);

  return {
    calendarOptions,
    queryParams,
    debounceHandleQuery,
    handlePagination,
    addMissingCalendar,
    isLoading,
  };
};

function formatCalendars(calendars: Calendar[]) {
  if (calendars && calendars.length) {
    return calendars.map((cal) => ({
      label: cal?.name || '',
      value: cal?.slug || '',
    }));
  }

  return [{ label: '', value: '' }];
}
